import { Input, Select } from 'components';
import React, { useEffect, useState } from 'react';
import { AuthService } from 'services';
import { Globals } from 'utils';

interface BasicCertData {
  cert: any;
  handleNext: (data?: any) => void;
  handleBack: () => void;
  apacuanaUser: any;
  useProfesionalInfo?: any;
}

// Regex: solo letras (con acentos y ñ) y espacios
const ONLY_LETTERS_REGEX = /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]*$/;

// Regex: solo dígitos, entre 6 y 9 caracteres (cédula y RIF)
const DOC_NUMBER_REGEX = /^\d{6,9}$/;

// Fecha máxima permitida para nacimiento: hoy menos 18 años (en formato YYYY-MM-DD)
const getMaxBirthdate = (): string => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return maxDate.toISOString().split('T')[0];
};

// Bloquea teclas no numéricas en inputs de solo número
const blockNonNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (
    ['e', 'E', '+', '-', '.', ','].includes(e.key) ||
    (e.key.length === 1 && !/[0-9]/.test(e.key))
  ) {
    e.preventDefault();
  }
};

// Estilo reutilizable para selects inline (mismos que Género/Tipo RIF ya tenían)
const selectStyle = (isEmpty: boolean, disabled = false): React.CSSProperties => ({
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid #ccc',
  fontSize: '0.95rem',
  color: isEmpty ? '#999' : '#1a1a1a',
  backgroundColor: disabled ? '#f0f0f0' : '#fff',
  outline: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#555',
  marginBottom: '6px',
};

const BasicDataCertificate: React.FC<BasicCertData> = ({
  cert,
  handleNext,
  handleBack,
  apacuanaUser,
  useProfesionalInfo = false,
}) => {
  const nameSplit = apacuanaUser?.full_name?.split(' ') ?? ['', ''];
  const documentSplit = apacuanaUser?.full_document?.split('-') ?? ['', ''];
  const maxBirthdate = getMaxBirthdate();

  // ── Estados y ciudades de la BD ────────────────────────────────────────
  const [statesList, setStatesList] = useState<{ id: number; name: string }[]>([]);
  const [citiesList, setCitiesList] = useState<{ id: number; name: string }[]>([]);
  const [stateId, setStateId] = useState<number | ''>('');

  useEffect(() => {
    const loadStates = async () => {
      try {
        const res: any = await AuthService.getDocumentTypes(); // trae states junto a document_types
        setStatesList(res.states || []);
      } catch {
        // Si falla, el select queda vacío
      }
    };
    loadStates();
  }, []);

  // Carga las ciudades cada vez que cambia el estado seleccionado
  useEffect(() => {
    const loadCities = async () => {
      if (!stateId) {
        setCitiesList([]);
        return;
      }
      try {
        const res: any = await AuthService.getCities({ state_id: stateId });
        setCitiesList(res.cities || []);
      } catch {
        setCitiesList([]);
      }
    };
    loadCities();
  }, [stateId]);

  // ── Campos personales pre-cargados (editables) ────────────────────────
  const [name, setName] = useState(nameSplit[0] ?? '');
  const [lastname, setLastname] = useState(nameSplit[1] ?? '');
  const [email, setEmail] = useState(apacuanaUser?.email ?? '');
  const [phone, setPhone] = useState(apacuanaUser?.full_phone ?? '');

  // Cédula: tipo y número separados
  const [kinddoc, setKinddoc] = useState(documentSplit[0] ?? '');
  const [doc, setDoc] = useState(documentSplit[1] ?? '');

  // state guarda el NAME del estado (string) para enviarlo a Apacuana
  const [state, setState] = useState(apacuanaUser?.state?.name ?? '');
  const [city, setCity] = useState(apacuanaUser?.city?.name ?? '');

  // ── Campos editables vacíos ────────────────────────────────────────────
  const [birthdate, setBirthdate] = useState('');
  const [gender, setGender] = useState('');
  const [kindrif, setKindrif] = useState('');
  const [rif, setRif] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [parish, setParish] = useState('');

  // ── Campos profesionales ───────────────────────────────────────────────
  const [occupation, setOccupation] = useState('');
  const [degree, setDegree] = useState('');
  const [university, setUniversity] = useState('');
  const [graduationyear, setGraduationyear] = useState('');
  const [collegiatenumber, setCollegiatenumber] = useState('');
  const [collegiateyear, setCollegiateyear] = useState('');

  // Maneja el cambio de estado: actualiza state/stateId y resetea ciudad, municipio y parroquia
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value ? Number(e.target.value) : '';
    const selected = statesList.find((s) => s.id === selectedId);

    setStateId(selectedId);
    setState(selected?.name ?? '');
    setCity('');
    setMunicipality('');
    setParish('');
  };

  const isStateSelected = stateId !== '';

  // ── Validaciones antes de avanzar ─────────────────────────────────────
  const validate = (): boolean => {
    if (!name.trim()) {
      Globals.showError('Por favor, ingrese su nombre');
      return false;
    }
    if (!ONLY_LETTERS_REGEX.test(name.trim())) {
      Globals.showError('El nombre solo debe contener letras');
      return false;
    }

    if (!lastname.trim()) {
      Globals.showError('Por favor, ingrese su apellido');
      return false;
    }
    if (!ONLY_LETTERS_REGEX.test(lastname.trim())) {
      Globals.showError('El apellido solo debe contener letras');
      return false;
    }

    if (!email.trim()) {
      Globals.showError('Por favor, ingrese su correo electrónico');
      return false;
    }

    if (!birthdate) {
      Globals.showError('Por favor, ingrese su fecha de nacimiento');
      return false;
    }
    if (birthdate > maxBirthdate) {
      Globals.showError('Debe ser mayor de edad (18 años) para continuar');
      return false;
    }

    if (!gender) {
      Globals.showError('Por favor, seleccione su género');
      return false;
    }

    // ── Cédula: tipo y número ──
    if (!kinddoc) {
      Globals.showError('Por favor, seleccione el tipo de documento');
      return false;
    }
    if (!doc.trim()) {
      Globals.showError('Por favor, ingrese su número de documento');
      return false;
    }
    if (!DOC_NUMBER_REGEX.test(doc.trim())) {
      Globals.showError('El número de documento debe tener entre 6 y 9 dígitos');
      return false;
    }

    // ── RIF: tipo y número (obligatorio) ──
    if (!kindrif) {
      Globals.showError('Por favor, seleccione el tipo de RIF');
      return false;
    }
    if (!rif.trim()) {
      Globals.showError('Por favor, ingrese su número de RIF');
      return false;
    }
    if (!DOC_NUMBER_REGEX.test(rif.trim())) {
      Globals.showError('El número de RIF debe tener entre 6 y 9 dígitos');
      return false;
    }

    if (!phone.trim()) {
      Globals.showError('Por favor, ingrese su teléfono');
      return false;
    }

    if (!state) {
      Globals.showError('Por favor, seleccione su estado');
      return false;
    }

    if (!city) {
      Globals.showError('Por favor, seleccione su ciudad');
      return false;
    }

    if (!municipality.trim()) {
      Globals.showError('Por favor, ingrese su municipio');
      return false;
    }

    if (!parish.trim()) {
      Globals.showError('Por favor, ingrese su parroquia');
      return false;
    }

    if (useProfesionalInfo) {
      if (!occupation.trim()) {
        Globals.showError('Por favor, ingrese su profesión');
        return false;
      }
      if (!degree.trim()) {
        Globals.showError('Por favor, ingrese su título');
        return false;
      }
      if (!university.trim()) {
        Globals.showError('Por favor, ingrese su universidad o institución');
        return false;
      }
      if (!graduationyear.trim()) {
        Globals.showError('Por favor, ingrese su año de graduación');
        return false;
      }
      if (!collegiatenumber.trim()) {
        Globals.showError('Por favor, ingrese su número de colegiatura');
        return false;
      }
      if (!collegiateyear.trim()) {
        Globals.showError('Por favor, ingrese su año de colegiado');
        return false;
      }
    }

    return true;
  };

  return (
    <div
      className='container-white'
      style={{ padding: '25px', borderRadius: '8px', minWidth: 650 }}
    >
      <h3
        style={{
          fontSize: '1.4rem',
          fontWeight: 600,
          color: '#333',
          marginBottom: '15px',
          paddingBottom: '10px',
        }}
      >
        Información Personal
      </h3>

      <div
        style={{
          marginBottom: '25px',
          padding: '15px',
          borderLeft: '4px solid #007bff',
          backgroundColor: '#eef4ff',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>✅</span>
        <p
          style={{
            margin: 0,
            color: '#011E4A',
            fontSize: '0.95rem',
            fontWeight: 500,
          }}
        >
          Verifique su información personal. Estos datos serán usados para la solicitud de su
          certificado electrónico.
        </p>
      </div>

      {/* Nombre y Apellido — solo letras */}
      <div className='row'>
        <div className='col-md-6'>
          <Input
            maxLength={100}
            type='text'
            color='white'
            label='Nombre'
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (ONLY_LETTERS_REGEX.test(e.target.value)) setName(e.target.value);
            }}
          />
        </div>
        <div className='col-md-6'>
          <Input
            maxLength={100}
            type='text'
            color='white'
            label='Apellido'
            value={lastname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (ONLY_LETTERS_REGEX.test(e.target.value)) setLastname(e.target.value);
            }}
          />
        </div>
      </div>

      {/* Correo y Fecha de nacimiento */}
      <div className='row'>
        <div className='col-md-6'>
          <Input
            maxLength={200}
            type='email'
            color='white'
            label='Correo Electrónico'
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          />
        </div>
        <div className='col-md-6'>
          <Input
            type='date'
            color='white'
            label='Fecha de nacimiento'
            value={birthdate}
            max={maxBirthdate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBirthdate(e.target.value)}
          />
        </div>
      </div>

      {/* Género y Cédula (Tipo + Número) */}
      <div className='row'>
        <div className='col-md-6'>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Género</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              style={selectStyle(gender === '')}
            >
              <option value=''>Seleccione género...</option>
              <option value='M'>Masculino</option>
              <option value='F'>Femenino</option>
            </select>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='container-select' style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: 90 }}>
              <label style={labelStyle}>Tipo Doc.</label>
              <select
                value={kinddoc}
                onChange={(e) => setKinddoc(e.target.value)}
                style={selectStyle(kinddoc === '')}
              >
                <option value=''>Tipo...</option>
                <option value='V'>V</option>
                <option value='E'>E</option>
                <option value='J'>J</option>
                <option value='G'>G</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                maxLength={9}
                type='text'
                color='white'
                label='Número de Documento'
                value={doc}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (/^\d*$/.test(e.target.value)) setDoc(e.target.value);
                }}
                onKeyDown={blockNonNumeric}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tipo RIF, Número de RIF y Teléfono — solo números */}
      <div className='row'>
        <div className='col-md-2'>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Tipo RIF</label>
            <select
              value={kindrif}
              onChange={(e) => setKindrif(e.target.value)}
              style={selectStyle(kindrif === '')}
            >
              <option value=''>Tipo...</option>
              <option value='V'>V</option>
              <option value='J'>J</option>
              <option value='E'>E</option>
              <option value='G'>G</option>
            </select>
          </div>
        </div>
        <div className='col-md-4'>
          <Input
            maxLength={9}
            type='text'
            color='white'
            label='Número de RIF'
            value={rif}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (/^\d*$/.test(e.target.value)) setRif(e.target.value);
            }}
            onKeyDown={blockNonNumeric}
          />
        </div>
        <div className='col-md-6'>
          <Input
            maxLength={11}
            type='text'
            color='white'
            label='Teléfono'
            value={phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (/^\d*$/.test(e.target.value)) setPhone(e.target.value);
            }}
            onKeyDown={blockNonNumeric}
          />
        </div>
      </div>

      {/* Estado (select desde BD) y Ciudad (select dependiente del estado) */}
      <div className='row'>
        <div className='col-md-6'>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Estado</label>
            <select
              value={stateId}
              onChange={handleStateChange}
              style={selectStyle(stateId === '')}
            >
              <option value=''>Seleccione un estado...</option>
              {statesList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='col-md-6'>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Ciudad</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!isStateSelected}
              style={selectStyle(city === '', !isStateSelected)}
            >
              <option value=''>
                {isStateSelected ? 'Seleccione una ciudad...' : 'Seleccione primero un estado'}
              </option>
              {citiesList.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Municipio y Parroquia — habilitados solo cuando hay estado seleccionado */}
      <div className='row'>
        <div className='col-md-6'>
          <Input
            maxLength={100}
            type='text'
            color='white'
            label='Municipio'
            value={municipality}
            disabled={!isStateSelected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMunicipality(e.target.value)}
          />
        </div>
        <div className='col-md-6'>
          <Input
            maxLength={100}
            type='text'
            color='white'
            label='Parroquia'
            value={parish}
            disabled={!isStateSelected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParish(e.target.value)}
          />
        </div>
      </div>

      {/* ── Información Profesional ── */}
      {useProfesionalInfo && (
        <>
          <h3
            style={{
              fontSize: '1.4rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '15px',
              paddingBottom: '10px',
            }}
          >
            Información Profesional
          </h3>

          <div
            style={{
              marginBottom: '25px',
              padding: '15px',
              borderLeft: '4px solid #007bff',
              backgroundColor: '#eef4ff',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '1.5rem', marginRight: '10px' }}>✅</span>
            <p
              style={{
                margin: 0,
                color: '#011E4A',
                fontSize: '0.95rem',
                fontWeight: 500,
              }}
            >
              Verifique su información profesional. Estos datos serán usados para la solicitud de su
              certificado electrónico.
            </p>
          </div>

          <div className='row'>
            <div className='col-md-6'>
              <Input
                maxLength={100}
                type='text'
                color='white'
                label='Profesión'
                value={occupation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOccupation(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <Input
                maxLength={200}
                type='text'
                color='white'
                label='Título'
                value={degree}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDegree(e.target.value)}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-6'>
              <Input
                maxLength={200}
                type='text'
                color='white'
                label='Universidad o institución'
                value={university}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUniversity(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              {/* Año de graduación — solo números, máx 4 dígitos */}
              <Input
                maxLength={4}
                type='text'
                color='white'
                label='Año de graduación'
                placeholder='2010'
                value={graduationyear}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (/^\d{0,4}$/.test(e.target.value)) setGraduationyear(e.target.value);
                }}
                onKeyDown={blockNonNumeric}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-6'>
              {/* Número de colegiatura — solo números */}
              <Input
                maxLength={20}
                type='text'
                color='white'
                label='Número de colegiatura'
                value={collegiatenumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (/^\d*$/.test(e.target.value)) setCollegiatenumber(e.target.value);
                }}
                onKeyDown={blockNonNumeric}
              />
            </div>
            <div className='col-md-6'>
              {/* Año de colegiado — solo números, máx 4 dígitos */}
              <Input
                maxLength={4}
                type='text'
                color='white'
                label='Año de colegiado'
                placeholder='2010'
                value={collegiateyear}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (/^\d{0,4}$/.test(e.target.value)) setCollegiateyear(e.target.value);
                }}
                onKeyDown={blockNonNumeric}
              />
            </div>
          </div>
        </>
      )}

      {/* ── Botones ── */}
      <div className='row' style={{ marginTop: '20px' }}>
        <div className='col-md-6'>
          <button
            className={`btn btn-report-2 ${''}`}
            onClick={() => handleBack()}
            style={{ width: '100%', background: '#011E4A !important' }}
          >
            <p>Regresar</p>
          </button>
        </div>

        <div className='col-md-6'>
          <button
            className={`btn btn-report ${'active'}`}
            onClick={() => {
              if (!validate()) return;

              const data = {
                email,
                typeuser: cert?.id,
                name,
                lastname,
                kinddoc,
                doc: Number(doc),
                birthdate,
                kindrif,
                rif,
                phone,
                state,
                city,
                municipality,
                parish,
                gender,
              };
              const dataProfessional = {
                occupation,
                degree,
                university,
                graduationyear,
                collegiatenumber,
                collegiateyear,
              };
              handleNext(useProfesionalInfo ? { ...data, ...dataProfessional } : data);
            }}
            style={{ width: '100%' }}
          >
            <p>Siguiente</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicDataCertificate;
